import type {
  AuditCourtesyMigrationOutput,
  CourtesyMigrationStage,
  MigrateCourtesyOutput,
} from '@etiquetachef/types';

/**
 * Peças puras da tela `/billing/courtesy-migration` (F7 da #679, #738): a
 * chave de simulação e os relatórios em Markdown.
 *
 * Origem: #738 — 2026-09-25 11:24 +0200 (migração da cortesia em etapas).
 *
 * Por quê: o relatório em Markdown é o que o owner cola na #679 antes de cada
 * etapa (§4.5: "relatório colado nesta issue antes de cada etapa"). A chave de
 * simulação é o que amarra o "Aplicar" à simulação que o operador acabou de
 * ver: mudar a etapa, os tenants ou o build mínimo invalida a simulação.
 *
 * Contrato: funções puras, sem rede. Não anonimizam — o relatório leva
 * `tenantId` e nome porque é colado numa issue privada do repositório.
 *
 * Usado por: `routes/CourtesyMigrationPage.tsx` (rota `/billing/courtesy-migration`,
 * menu Cobrança; spec `docs/screens/admin-courtesy-migration.md`), com as saídas das
 * callables `auditCourtesyMigration` e `migrateCourtesy`
 * (`apps/functions/src/admin/courtesyMigration.ts`).
 *
 * Dados: `AuditCourtesyMigrationOutput`, `MigrateCourtesyOutput` e
 * `CourtesyMigrationStage` em `packages/types/src/courtesyMigration.ts`.
 * `generatedAt` é epoch ms; `before`/`after` são o trecho do doc
 * `billing/subscription` que a etapa toca.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) rota, menu e spec; doc da `simulationKey` (#796).
 */

/**
 * Chave que identifica uma simulação: etapa + tenants (ordenados) + builds mínimos.
 *
 * Origem: #738 — 2026-09-25 11:24 +0200.
 *
 * Por quê: o "Aplicar" só habilita se a chave atual for a da última simulação
 * — mudar etapa, tenants ou build mínimo obriga a simular de novo. Tenants são
 * ordenados para a ordem de seleção não invalidar a simulação.
 *
 * Usado por: `routes/CourtesyMigrationPage.tsx` (`currentKey`).
 */
export function simulationKey(args: {
  stage: CourtesyMigrationStage;
  tenantIds: readonly string[];
  minBuilds?: { android: number; ios: number } | undefined;
}): string {
  return JSON.stringify([
    args.stage,
    [...args.tenantIds].sort(),
    args.minBuilds ? [args.minBuilds.android, args.minBuilds.ios] : null,
  ]);
}

const yesNo = (v: boolean): string => (v ? 'aberto' : '**FECHADO**');

/** Tabela Markdown da auditoria (gravado × efetivo × gates × dispositivos por tenant). */
export function auditMarkdown(audit: AuditCourtesyMigrationOutput): string {
  const lines = [
    `### Auditoria da migração da cortesia — ${new Date(audit.generatedAt).toISOString()}`,
    '',
    '| Tenant | Nome | Gravado | Efetivo | isDemo | sub_ | Stripe | Gate legado | Gate novo | Dispositivos |',
    '| --- | --- | --- | --- | --- | --- | --- | --- | --- | --- |',
    ...audit.rows.map(
      (r) =>
        '| ' +
        [
          r.tenantId,
          r.tenantName,
          r.storedMode ?? '—',
          r.effectiveMode,
          r.isDemoFlag ? 'sim' : 'não',
          r.externalSubscriptionId ?? '—',
          r.gatewayStatus ?? '—',
          yesNo(r.legacyGateOpen),
          yesNo(r.newGateOpen),
          r.devices.length === 0
            ? '—'
            : r.devices.map((d) => `${d.platform} ${d.appVersion || '?'}`).join('; '),
        ].join(' | ') +
        ' |',
    ),
    '',
    `Venda direta sem \`commercial_mode\` gravado: ${String(audit.directWithoutStoredMode.length)}` +
      (audit.directWithoutStoredMode.length > 0 ? ` (${audit.directWithoutStoredMode.join(', ')})` : ''),
  ];
  return lines.join('\n');
}

/** Tabela Markdown de uma etapa (simulada ou aplicada), com pulados e falhas. */
export function migrationMarkdown(out: MigrateCourtesyOutput, reason: string): string {
  const lines = [
    `### ${out.stage} — ${out.dryRun ? 'simulação' : 'aplicado'}`,
    '',
    `Motivo: ${reason}`,
    '',
    '| Tenant | Antes | Depois | Stripe |',
    '| --- | --- | --- | --- |',
    ...out.rows.map(
      (r) =>
        `| ${r.tenantId} | \`${JSON.stringify(r.before)}\` | \`${JSON.stringify(r.after)}\` | ${r.gateway ?? '—'} |`,
    ),
  ];
  if (out.skipped.length > 0) {
    lines.push('', 'Pulados:', ...out.skipped.map((s) => `- ${s.tenantId}: ${s.why}`));
  }
  if (out.failed.length > 0) {
    lines.push('', 'Falharam:', ...out.failed.map((f) => `- ${f.tenantId}: ${f.error}`));
  }
  return lines.join('\n');
}
